import { useState, useMemo, useEffect, useRef, forwardRef } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { ko } from 'date-fns/locale';

import dropdownIcon from '@shared/assets/images/dropdown_icon.svg';
import chevronDownIcon from '@shared/assets/images/chevron_down_icon.svg';
import {
  MobileApprovalList,
  MobileFilterSheet,
  MobileSelectScreen,
} from '@shared/components/organisms/mobile';
import searchFilterIcon from '@shared/assets/images/search-filter-white_Icon.svg';

import styles from '@pages/vacation/components/mobile/MobileVacationApprovalContent/MobileVacationApprovalContent.module.scss';

function formatToYYYYMMDD(date) {
  if (!date) return null;

  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');

  return `${yyyy}${mm}${dd}`;
}

const DateButtonInput = forwardRef(function DateButtonInput(
  { value, onClick, placeholder, className, disabled },
  ref,
) {
  return (
    <button type="button" ref={ref} onClick={onClick} disabled={disabled} className={className}>
      <span className={value ? styles.dateValue : styles.datePlaceholder}>
        {value || placeholder}
      </span>
    </button>
  );
});

/**
 * 모바일 휴가 신청 리스트 아이템 (텍스트 영역 전용)
 * - renderText 가독성 개선을 위해 분리
 */
function MobileVacationListItem({ item }) {
  /**
   * 휴가 유형 라벨
   * 예)
   * [연차]
   * [연차 - 종일]
   */
  const vacationTypeLabel = useMemo(() => {
    if (!item?.vacation_type?.name) {
      return '';
    }

    if (!item.cc_type?.name) {
      return `[${item.vacation_type.name}]`;
    }

    return `[${item.vacation_type.name} - ${item.cc_type.name}]`;
  }, [item]);

  /**
   * 작성자 정보 라벨
   * 예)
   * 플랫폼개발팀 - 유해정
   */
  const writerLabel = useMemo(() => {
    const team = item?.writerInfo?.team;
    const name = item?.writerInfo?.name;

    if (!team && !name) {
      return '';
    }

    if (!team) {
      return name;
    }

    if (!name) {
      return team;
    }

    return `${team} - ${name}`;
  }, [item]);

  return (
    <div className={styles.textFrame}>
      <span className={styles.title}>
        {vacationTypeLabel} {item.reason}
      </span>
      <span className={styles.subtitle}>{writerLabel}</span>
      <span className={styles.subtitle}>
        휴가일정 {item.start_date} ~ {item.end_date}
      </span>

      <span className={styles.subtitle}>신청일 {item.date}</span>
    </div>
  );
}

export default function MobileVacationApprovalContent({
  isLoading,
  vacationData,
  searchParams,
  onSearchParams,
  defaultStartDate,
  defaultEndDate,
  onItemClick,
  vacationTypes,
  teamList,
}) {
  const [sortValue, setSortValue] = useState('latest');
  const [isSortOpen, setIsSortOpen] = useState(false);
  const sortDropdownRef = useRef(null);

  // 정렬 드롭다운: 외부 터치/클릭 시 닫기 (모바일 포함)
  useEffect(function () {
    function handlePointerOutside(event) {
      if (sortDropdownRef.current && !sortDropdownRef.current.contains(event.target)) {
        setIsSortOpen(false);
      }
    }

    document.addEventListener('mousedown', handlePointerOutside);
    document.addEventListener('touchstart', handlePointerOutside, { passive: true });
    return function () {
      document.removeEventListener('mousedown', handlePointerOutside);
      document.removeEventListener('touchstart', handlePointerOutside);
    };
  }, []);

  const [startDate, setStartDate] = useState(defaultStartDate);
  const [endDate, setEndDate] = useState(defaultEndDate);

  // 필터 관련 상태
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [isSelectOpen, setIsSelectOpen] = useState(false);
  const [selectField, setSelectField] = useState(null);

  function formatDate(date) {
    if (!date) return '';
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(
      date.getDate(),
    ).padStart(2, '0')}`;
  }

  // 필터 값 상태
  const [filterValues, setFilterValues] = useState({
    queryPeriod_start: formatDate(startDate),
    queryPeriod_end: formatDate(endDate),
    approvalStatus: '', // 전체
    vacationType: '', // 전체
    teamCode: '', // 전체
    name: '', // 전체
  });
  const filterFields = useMemo(() => {
    // 결재상태
    const statusOptions = [
      { value: '', label: '전체' },
      { value: '2', label: '결재중' },
      { value: '3', label: '결재완료' },
      { value: '4', label: '반려' },
    ];

    // 휴가구분
    const vacationTypeOptions = [
      { value: '', label: '전체' },
      ...vacationTypes.map((type) => ({
        value: type.code?.toString() || '',
        label: type.name || '',
      })),
    ];

    // 기안부서
    const teamOptions = [
      { value: '', label: '전체' },
      ...teamList.map((team) => ({
        value: team.teamCode?.toString() || '',
        label: team.team || '',
      })),
    ];

    return [
      {
        key: 'queryPeriod',
        type: 'dateRange',
        label: '조회기간',
        startKey: 'queryPeriod_start',
        endKey: 'queryPeriod_end',
        startValue: filterValues.queryPeriod_start || '',
        endValue: filterValues.queryPeriod_end || '',
      },
      {
        key: 'approvalStatus',
        type: 'select',
        label: '결재상태',
        value: filterValues.approvalStatus || '',
        options: statusOptions,
      },
      {
        key: 'vacationType',
        type: 'select',
        label: '휴가구분',
        value: filterValues.vacationType || '',
        options: vacationTypeOptions,
      },
      {
        key: 'teamCode',
        type: 'select',
        label: '기안부서',
        value: filterValues.teamCode || '',
        options: teamOptions,
      },
      {
        key: 'name',
        type: 'input',
        label: '기안자',
        value: filterValues.name || '',
        placeholder: '이름 입력',
      },
    ];
  }, [filterValues, vacationTypes, teamList]);

  // 필터 버튼 클릭
  function handleFilterClick() {
    setIsFilterOpen(true);
  }

  // 필터 값 변경 (객체)
  function handleFilterChange(updatedValues) {
    setFilterValues((prev) => ({
      ...prev,
      ...updatedValues,
    }));
  }

  // 필터 초기화
  function handleFilterReset() {
    setStartDate(defaultStartDate);
    setEndDate(defaultEndDate);

    setFilterValues({
      queryPeriod_start: formatDate(startDate),
      queryPeriod_end: formatDate(endDate),
      approvalStatus: '', // 전체
      vacationType: '', // 전체
      teamCode: '', // 전체
      name: '', // 전체
    });

    onSearchParams({
      startDate: formatToYYYYMMDD(defaultStartDate),
      endDate: formatToYYYYMMDD(defaultEndDate),
      stateCode: null,
      vacationType: null,
      teamCode: null,
      name: null,
    });
  }

  // 셀렉트 필드 클릭 (풀스크린 선택 화면 열기)
  function handleSelectFieldClick(field, currentValue) {
    setSelectField({
      ...field,
      currentValue: currentValue,
    });
    setIsSelectOpen(true);
  }

  // 셀렉트 값 변경
  function handleSelectChange(value) {
    if (selectField) {
      setFilterValues(function (prev) {
        return { ...prev, [selectField.key]: value };
      });
    }
    setIsSelectOpen(false);
    setSelectField(null);
  }

  //날짜 변동시 상태값 수정
  // useEffect(() => {
  //   setStartDate(defaultStartDate);
  //   setEndDate(defaultEndDate);
  // }, [defaultStartDate, defaultEndDate]);

  /** 정렬 옵션 */
  const sortOptions = [
    { value: 'latest', label: '신청일 최신순' },
    { value: 'oldest', label: '신청일 오래된순' },
  ];

  /** 선택된 정렬 라벨 */
  const selectedSortLabel =
    sortOptions.find((opt) => opt.value === sortValue)?.label ?? '신청일 최신순';

  function handleSortSelect(value) {
    setSortValue(value);
    setIsSortOpen(false);
  }

  //조회 트리거
  function handleKeyDown(event) {
    if (event && event.key !== 'Enter') return;

    if (!startDate || !endDate) return;

    onSearchParams({
      startDate: formatToYYYYMMDD(startDate),
      endDate: formatToYYYYMMDD(endDate),
      stateCode: null,
      vacationType: null,
    });
  }

  function handleApplyFilter(latestValues) {
    // 바텀시트에서 넘어온 최신 localValues를 사용 (없으면 기존 filterValues 사용)
    const currentValues = latestValues || filterValues;

    const startStr = currentValues.queryPeriod_start || '';
    const endStr = currentValues.queryPeriod_end || '';

    // 1. 상단 DatePicker 상태 동기화 (UI 업데이트)
    if (startStr) setStartDate(new Date(startStr));
    if (endStr) setEndDate(new Date(endStr));

    // 2. API 검색 (YYYY-MM-DD -> YYYYMMDD)
    const params = {
      startDate: startStr.replace(/-/g, ''),
      endDate: endStr.replace(/-/g, ''),
      stateCode: currentValues.approvalStatus === '' ? null : Number(currentValues.approvalStatus),
      vacationType: currentValues.vacationType === '' ? null : Number(currentValues.vacationType),
      teamCode: currentValues.teamCode === '' ? null : Number(currentValues.teamCode),
      name: currentValues.name?.trim() || null,
    };

    onSearchParams(params);
    setIsFilterOpen(false);
  }

  // 부모 searchParams가 바뀌면 UI(DatePicker + 바텀시트) 동기화
  useEffect(() => {
    if (!searchParams?.startDate || !searchParams?.endDate) return;

    const start = new Date(
      `${searchParams.startDate.slice(0, 4)}-${searchParams.startDate.slice(
        4,
        6,
      )}-${searchParams.startDate.slice(6, 8)}`,
    );

    const end = new Date(
      `${searchParams.endDate.slice(0, 4)}-${searchParams.endDate.slice(
        4,
        6,
      )}-${searchParams.endDate.slice(6, 8)}`,
    );

    // DatePicker
    setStartDate(start);
    setEndDate(end);

    // 바텀시트
    setFilterValues((prev) => ({
      ...prev,
      queryPeriod_start: formatDate(start),
      queryPeriod_end: formatDate(end),
    }));
  }, [searchParams]);

  /**
   * 필터링 + 정렬
   * - 현재는 필터 조건 없음
   */
  const filteredAndSortedData = useMemo(() => {
    if (!Array.isArray(vacationData) || vacationData.length === 0) {
      return [];
    }
    // 검색어 및 필터 적용
    const filtered = vacationData.filter(() => true);

    return filtered.sort((a, b) => {
      const dateA = a?.date ?? '';
      const dateB = b?.date ?? '';

      if (sortValue === 'latest') {
        return dateB.localeCompare(dateA);
      }

      return dateA.localeCompare(dateB);
    });
  }, [vacationData, sortValue]);

  return (
    <>
      <div className={styles.container}>
        <div className={styles.headerContainer}>
          <span className={styles.headerSpan}>신청일</span>
          <div className={styles.headerSearch}>
            <div className={styles.headerSearchContent}>
              <div className={styles.dateInputWrapper}>
                <DatePicker
                  selected={startDate}
                  onChange={(date) => {
                    //바텀시트 문자열도 동기화
                    setFilterValues((prev) => ({
                      ...prev,
                      queryPeriod_start: formatDate(date),
                    }));
                    setStartDate(date);
                    if (!isFilterOpen) {
                      onSearchParams({
                        startDate: formatToYYYYMMDD(date),
                        endDate: formatToYYYYMMDD(endDate),
                        stateCode: searchParams.stateCode ?? null,
                        vacationType: searchParams.vacationType ?? null,
                        teamCode: searchParams.teamCode ?? null,
                        name: searchParams.name ?? null,
                      });
                    }
                  }}
                  locale={ko}
                  dateFormat="yyyy-MM-dd"
                  customInput={
                    <DateButtonInput className={styles.dateInput} placeholder={startDate} />
                  }
                  maxDate={endDate}
                  withPortal
                  portalId="mobile-datepicker-portal"
                />
                <img src={dropdownIcon} alt="" className={styles.dateIcon} />
              </div>
              <div className={styles.selectDivider} />
            </div>

            <span className={styles.dateSeparator}>~</span>
            <div className={styles.headerSearchContent}>
              <div className={styles.dateInputWrapper}>
                <DatePicker
                  selected={endDate}
                  onChange={(date) => {
                    //바텀시트 문자열도 동기화
                    setFilterValues((prev) => ({
                      ...prev,
                      queryPeriod_end: formatDate(date),
                    }));
                    setEndDate(date);
                    if (!isFilterOpen) {
                      onSearchParams({
                        startDate: formatToYYYYMMDD(startDate),
                        endDate: formatToYYYYMMDD(date),
                        stateCode: searchParams.stateCode ?? null,
                        vacationType: searchParams.vacationType ?? null,
                        teamCode: searchParams.teamCode ?? null,
                        name: searchParams.name ?? null,
                      });
                    }
                  }}
                  locale={ko}
                  dateFormat="yyyy-MM-dd"
                  customInput={
                    <DateButtonInput className={styles.dateInput} placeholder={endDate} />
                  }
                  minDate={startDate}
                  withPortal
                  portalId="mobile-datepicker-portal"
                />
                <img src={dropdownIcon} alt="" className={styles.dateIcon} />
              </div>
              <div className={styles.selectDivider} />
            </div>
            <div className={styles.headerSearchRight}>
              <button className={styles.headerSearchBtn} onClick={() => handleFilterClick()}>
                <img src={searchFilterIcon} alt="검색" width={19} height={19} />
              </button>
            </div>
          </div>
        </div>
        <div className={styles.listSection}>
          {/* 정렬 드롭다운 */}
          <div className={styles.sortWrapper} ref={sortDropdownRef}>
            <button
              type="button"
              className={styles.sortButton}
              onClick={() => setIsSortOpen((prev) => !prev)}
            >
              <span className={styles.sortLabel}>{selectedSortLabel}</span>

              <img
                src={chevronDownIcon}
                alt=""
                width={16}
                height={16}
                className={`${styles.sortChevron} ${isSortOpen ? styles.sortChevronOpen : ''}`}
              />
            </button>

            {isSortOpen && (
              <ul className={styles.sortDropdown}>
                {sortOptions.map((option) => (
                  <li key={option.value}>
                    <button
                      type="button"
                      className={`${styles.sortOption} ${
                        sortValue === option.value ? styles.sortOptionActive : ''
                      }`}
                      onClick={() => handleSortSelect(option.value)}
                    >
                      {option.label}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* 리스트 */}
          <div className={styles.listWrapper}>
            {isLoading ? (
              <div className={styles.loadingContainer}>
                <div className={styles.circularProgress} aria-label="휴가 결재내역 로딩 중" />
                <div className={styles.loadingMessage}>휴가 결재 내역 불러오는 중...</div>
              </div>
            ) : (
              <MobileApprovalList
                items={filteredAndSortedData}
                onItemClick={onItemClick}
                emptyMessage="휴가결재 내역이 존재하지 않습니다."
                renderText={(item) => <MobileVacationListItem item={item} />}
              />
            )}
          </div>
        </div>
        {/* 필터 바텀시트 */}
        <MobileFilterSheet
          isOpen={isFilterOpen}
          onClose={() => setIsFilterOpen(false)}
          filterFields={filterFields}
          filterValues={filterValues}
          onFilterChange={handleFilterChange}
          onApply={handleApplyFilter}
          onReset={handleFilterReset}
          onSelectFieldClick={handleSelectFieldClick}
        />
        {/* 셀렉트 풀스크린 */}
        <MobileSelectScreen
          isOpen={isSelectOpen}
          onClose={() => {
            setIsSelectOpen(false);
            setSelectField(null);
          }}
          title={selectField?.label || '선택'}
          options={selectField?.options || []}
          value={selectField?.currentValue}
          onChange={handleSelectChange}
        />
      </div>
    </>
  );
}
